import { consentConfig } from '@/shared/Config'

/*
 * OVHcloud CMP (Consent Management Platform).
 *
 * The bundle is injected after the app boots (see src/shared/Analytics/index.js, called
 * from every main.js) rather than declared in index.html, so its URL and settings stay in
 * Config.js / docker-bake.hcl with the rest of the per-environment configuration.
 *
 * `cmp.iife.js` is only a small loader: it reads its own document.currentScript.src to
 * derive a base URL, fetches version.json and then injects the real versioned bundle. That
 * works for a dynamically inserted classic script, since currentScript is set while the
 * script is initially evaluated — so we inject the loader and let it do its job instead of
 * reimplementing the version lookup here.
 *
 * NOTE: named "Consent" rather than "Cmp" on purpose — `config.CMP`, CHECK_CMP_SESSION and
 * LOGOUT_CMP already mean something unrelated in this codebase (the account/session
 * platform).
 */

// BCP-47 locales the CMP publishes, keyed by our own i18n language codes. An unmapped
// language (e.g. jp) is left undeclared so the CMP falls back to navigator.language and
// then en-GB, per its documented resolution chain.
const CMP_LOCALES = {
  en: 'en-GB',
  fr: 'fr-FR'
}

// How long to wait for the bundle before giving up. The CMP fails loud with no fallback
// (missing version.json, blocked by an extension, offline...), so callers must never be
// left hanging on a bundle that will never arrive.
const READY_TIMEOUT = 5000

let loadPromise = null

const isAvailable = () => typeof window.__cmp === 'function'

const call = (command, ...args) => {
  if (!isAvailable()) {
    console.warn(`[Consent] window.__cmp is unavailable, skipping "${command}"`)
    return null
  }
  return window.__cmp(command, ...args)
}

export default {
  isAvailable,

  /*
   * Declares the host configuration and injects the CMP loader once. __cmpConfig must be set
   * before the bundle runs: it is read once at its module init.
   * NOTE: never declare `scripts` here. The deployed CMP injects them while it boots, before
   * any consent and before our jQuery chunk (DATATR-3989). The tracking container is added
   * by AnalyticsClient.enable() instead.
   */
  load () {
    if (loadPromise) return loadPromise

    const { url, environment, region } = consentConfig()
    if (!url) {
      console.warn('[Consent] no CMP url configured, consent banner disabled')
      return Promise.resolve()
    }

    window.__cmpConfig = { ...window.__cmpConfig, region, environment }

    loadPromise = new Promise(resolve => {
      if (document.querySelector(`script[src="${url}"]`)) {
        resolve()
        return
      }
      const script = document.createElement('script')
      script.src = url
      script.async = true
      script.onload = () => resolve()
      script.onerror = () => {
        console.error(`[Consent] failed to load the CMP bundle from ${url}`)
        resolve()
      }
      document.head.appendChild(script)
    })

    return loadPromise
  },

  // Resolves once window.__cmp exists, or after `timeout` if it never shows up.
  // Check isAvailable() afterwards to know which of the two happened.
  ready (timeout = READY_TIMEOUT) {
    if (isAvailable()) return Promise.resolve()

    return new Promise(resolve => {
      let timeoutId = null
      const done = () => {
        clearTimeout(timeoutId)
        window.removeEventListener('cmp:ready', done)
        resolve()
      }
      timeoutId = setTimeout(() => {
        console.warn(`[Consent] CMP bundle did not load within ${timeout}ms`)
        done()
      }, timeout)
      window.addEventListener('cmp:ready', done, { once: true })
    })
  },

  // { technical, exempt, analytics, marketing } once a choice was made, else null.
  // technical and exempt are declared required by the policy, so they are always true —
  // the only real decisions are analytics and marketing.
  getConsent () {
    return call('getConsent')
  },

  // Fires on consent:collected / consent:updated / consent:withdrawn.
  // Returns the CMP's unsubscribe function (null when unavailable).
  onConsentChange (callback) {
    return call('onConsentChange', callback)
  },

  // Opens the preferences modal — the "manage my cookies" entry points.
  showPreferences () {
    return call('showPreferences')
  },

  getPolicyVersion () {
    return call('getPolicyVersion')
  },

  // The CMP re-reads __cmpConfig.locale on every modal open, so mutating it is enough for a
  // client-side language switch to apply to the *next* open.
  setLocale (lang) {
    const locale = CMP_LOCALES[lang]
    if (!locale || !window.__cmpConfig) return
    window.__cmpConfig.locale = locale
  }
}
